/**
 * module_entitlement_storage — état des modules du locataire tel que la Marketplace le lit
 * (`GET /modules/activations` + `GET /modules/licenses`), et son snapshot `localStorage`
 * (`tenant_module_entitlements`).
 *
 * UPT-2069 (L1) : ce fichier ne DÉCIDE plus rien. La question « ce module est-il ouvert ? » a une seule réponse,
 * l'auth-context servi par le serveur (`useActiveModules()` / `isModuleOpen()`, uptk/core/modules/activeModules.ts) ;
 * le snapshot n'est plus qu'un cache d'affichage au premier rendu, avant l'arrivée de l'auth-context. Les anciennes
 * fonctions de décision (`isModuleEntitledForTenant`, `isSiteEntitledForTenant`, `isModuleEntitledSnapshot`,
 * `isModuleEntitledState`, `isModuleEntitledForApiUrl`), dont le « fail-open » laissait tout passer sans snapshot,
 * sont supprimées ; les clés du snapshot sont les codes réels normalisés (`normalizeModuleKey`).
 */
import type { TenantModuleActivation, TenantModuleLicense } from '@/uptk/core/components/marketplace/types';
import { normalizeModuleKey } from '@/uptk/core/modules/moduleKey';

export type ModuleEntitlementStatus = 'active' | 'inactive' | 'installed' | 'not_installed';

export type ModuleEntitlementRecord = {
  moduleCode: string;
  status: ModuleEntitlementStatus;
  isActive: boolean;
  isInstalled: boolean;
  isDisabled: boolean;
  licenseStatus?: string | null;
  licenseType?: string | null;
  validUntil?: string | null;
};

export type ModuleEntitlementSnapshot = {
  tenantId: string;
  updatedAt: string;
  entitlements: Record<string, ModuleEntitlementRecord>;
};

export type ModuleEntitlementsState = {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  tenantId: string | null;
  updatedAt: string | null;
  entitlements: Record<string, ModuleEntitlementRecord>;
  error: string | null;
};

const storageKey = 'tenant_module_entitlements';

const getTenantId = (tenantId?: string) => {
  if (typeof window === 'undefined') {
    return tenantId ?? '';
  }
  const stored = localStorage.getItem('tenantid') ?? '';
  return (tenantId ?? stored).trim();
};

const resolveActivationFlags = (activation?: TenantModuleActivation) => {
  if (!activation) {
    return { isDisabled: false, isActive: false, isInstalled: false };
  }
  // Le serveur sérialise l'énumération en majuscules (`ACTIVE`, `TRIAL`) : comparaison insensible à la casse.
  // UPT-2069 (L0, décision 4) : règle unique « actif ou essai = module ouvert ».
  const status = (activation.status ?? '').toString().trim().toLowerCase();
  const isEntitledStatus = status === 'active' || status === 'trial';
  const isDisabled = Boolean(activation.disabled || status === 'disabled' || status === 'inactive');
  const isActive = Boolean(activation.activated || isEntitledStatus);
  const isInstalled = Boolean(activation.installed || status === 'installed' || activation.activated || isEntitledStatus);
  return { isDisabled, isActive: !isDisabled && isActive, isInstalled: !isDisabled && isInstalled };
};

const resolveStatus = (flags: { isDisabled: boolean; isActive: boolean; isInstalled: boolean }): ModuleEntitlementStatus => {
  if (flags.isDisabled) {
    return 'inactive';
  }
  if (flags.isActive) {
    return 'active';
  }
  if (flags.isInstalled) {
    return 'installed';
  }
  return 'not_installed';
};

export const buildModuleEntitlementSnapshot = (
  tenantId: string,
  activations: TenantModuleActivation[],
  licenses: TenantModuleLicense[]
): ModuleEntitlementSnapshot => {
  const activationMap = new Map<string, TenantModuleActivation>();
  activations.forEach((activation) => {
    activationMap.set(normalizeModuleKey(activation.moduleCode), activation);
  });
  const licenseMap = new Map<string, TenantModuleLicense>();
  licenses.forEach((license) => {
    licenseMap.set(normalizeModuleKey(license.moduleCode), license);
  });

  const moduleCodes = new Set<string>([...activationMap.keys(), ...licenseMap.keys()]);
  moduleCodes.delete('');
  const entitlements: Record<string, ModuleEntitlementRecord> = {};

  moduleCodes.forEach((code) => {
    const activation = activationMap.get(code);
    const license = licenseMap.get(code);
    const flags = resolveActivationFlags(activation);
    const status = resolveStatus(flags);

    entitlements[code] = {
      moduleCode: code,
      status,
      isActive: flags.isActive,
      isInstalled: flags.isInstalled,
      isDisabled: flags.isDisabled,
      licenseStatus: license?.status ?? null,
      licenseType: license?.licenseType ?? null,
      validUntil: license?.validUntil ?? null,
    };
  });

  return {
    tenantId,
    updatedAt: new Date().toISOString(),
    entitlements,
  };
};

export const saveModuleEntitlementSnapshot = (snapshot: ModuleEntitlementSnapshot) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(storageKey, JSON.stringify(snapshot));
  } catch {
    // Stockage plein ou bloqué : le snapshot n'est qu'un cache, l'auth-context décide.
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object';
};

/** Le snapshot du locataire courant (cache de premier rendu), ou `null` (absent, illisible, autre locataire). */
export const loadModuleEntitlementSnapshot = (tenantId?: string): ModuleEntitlementSnapshot | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storageKey);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) {
      return null;
    }
    const snapshot = parsed as ModuleEntitlementSnapshot;
    const expectedTenant = getTenantId(tenantId);
    if (expectedTenant && snapshot.tenantId && snapshot.tenantId !== expectedTenant) {
      return null;
    }
    if (!snapshot.entitlements || typeof snapshot.entitlements !== 'object') {
      return null;
    }
    return snapshot;
  } catch {
    return null;
  }
};
